import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import styled, { css } from "styled-components";
import { useFocusRing } from "@react-aria/focus";
import { IconCancel } from "@bynder/icons";
import { token } from "./token.js";
import { textStyles } from "./textStyles.js";
import { makeInternalTokens } from "./makeInternalTokens.js";
const Chip = forwardRef(({
  size = "m",
  isDisabled = false,
  thumbnail,
  children,
  icon,
  onClick,
  onDelete,
  ...props
}, ref) => {
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing();
  function handleOnKeyDown(e) {
    if (onClick && e.key === "Enter") {
      e.preventDefault();
      onClick();
    } else if (onDelete && (e.key === "Backspace" || e.key === "Delete")) {
      onDelete();
    }
  }
  function handleOnDelete(event) {
    event.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  }
  const isButton = Boolean(onClick || onDelete);
  return /* @__PURE__ */ jsxs(ChipStyled, {
    $size: size,
    $isDisabled: isDisabled,
    disabled: isDisabled && isButton,
    onClick,
    onKeyDown: handleOnKeyDown,
    $isFocusVisible: isFocusVisible,
    as: isButton ? "button" : "span",
    ref,
    ...focusProps,
    ...props,
    children: [thumbnail && /* @__PURE__ */ jsx(ThumbnailContainer, {
      children: thumbnail
    }), icon && /* @__PURE__ */ jsx(IconContainer, {
      children: icon
    }), /* @__PURE__ */ jsx("span", {
      children
    }), onDelete && /* @__PURE__ */ jsx(DeleteButton, {
      $isDisabled: isDisabled,
      onClick: handleOnDelete,
      "data-testid": "ds-chip-delete-button",
      children: /* @__PURE__ */ jsx(IconCancel, {
        size: "18px"
      })
    })]
  });
});
const {
  internalTokens,
  declareTokens
} = makeInternalTokens({
  lightTheme: {
    "chip-background-color": token.gray50,
    "chip-background-color-disabled": token.gray25,
    "chip-background-color-hover": token.gray75,
    "chip-background-color-active": token.gray100,
    "chip-background-color-focus": token.gray25,
    "chip-shadow-focus": token.elevationStatic
  },
  darkTheme: {
    "chip-background-color": token.gray50a,
    "chip-background-color-disabled": token.gray25a,
    "chip-background-color-hover": token.gray75a,
    "chip-background-color-active": token.gray100a,
    "chip-background-color-focus": "transparent",
    "chip-shadow-focus": `0 0 0 1px ${token.gray200a}`
  }
});
const DeleteButton = styled.span.withConfig({
  displayName: "DeleteButton",
  componentId: "sc-1hj94p7-0"
})(["display:flex;justify-content:center;align-items:center;width:24px;height:24px;margin:0 calc(", " * -1) 0 ", ";padding:0;border:0;border-radius:", ";background:none;color:", ";outline:0;cursor:pointer;&:hover{background:", ";color:", ";}&:active{background:", ";}"], token.spacing3, token.spacing3, token.radiusCircle, (props) => props.$isDisabled ? token.gray400 : token.gray600, token.gray50a, token.gray800, token.gray100a);
const ChipStyled = styled.button.withConfig({
  displayName: "ChipStyled",
  componentId: "sc-1hj94p7-1"
})(["", " display:inline-flex;align-items:center;border:0;border-radius:", ";background-color:", ";color:", ";outline:none;font-family:", ";white-space:nowrap;", ";", ";", ";", ";", ";"], declareTokens, token.radiusPill, internalTokens["chip-background-color"], token.colorText, token.brandFontStackBase, (props) => props.onClick && css(["cursor:pointer;&:hover{background-color:", ";", "{color:", ";}}&:active{background-color:", ";}"], internalTokens["chip-background-color-hover"], DeleteButton, token.gray800, internalTokens["chip-background-color-active"]), (props) => props.$isFocusVisible && css(["background:", ";box-shadow:inset ", ";outline:0;"], internalTokens["chip-background-color-focus"], internalTokens["chip-shadow-focus"]), (props) => props.$size === "m" && MediumChip, (props) => props.$size === "s" && SmallChip, (props) => props.$isDisabled && DisabledChip);
const ThumbnailContainer = styled.div.withConfig({
  displayName: "ThumbnailContainer",
  componentId: "sc-1hj94p7-2"
})(["display:block;"]);
const SmallChip = css(["", " height:32px;padding:0 12px;", "{margin:0 ", " 0 calc(", " * -1);}"], textStyles.uiTextS, ThumbnailContainer, token.spacing3, token.spacing3);
const MediumChip = css(["", " height:40px;padding:0 16px;", "{margin:0 ", " 0 calc(", " * -1);}"], textStyles.uiTextM, ThumbnailContainer, token.spacing3, token.spacing4);
const DisabledChip = css(["background-color:", ";color:", ";pointer-events:none;"], internalTokens["chip-background-color-disabled"], token.colorTextDisabled);
const IconContainer = styled.div.withConfig({
  displayName: "IconContainer",
  componentId: "sc-1hj94p7-3"
})(["width:24px;height:24px;margin-left:calc(", " * -1);padding:0 ", " 0 0;"], token.spacing2, token.spacing3);
export {
  Chip
};
//# sourceMappingURL=Chip.js.map
